import { ExploreActions } from '@/components/explore/ExploreActions';
import { ExploreNavBreadcrumbs } from '@/components/explore/ExploreNavBreadcrumbs';
import { ExploreOutletContext } from '@/components/layouts/Layout';
import { Button } from '@/components/ui/button';
import { ContentActivityBadge } from '@/components/widget/content-activity-badge';
import { ContentCode } from '@/components/widget/content-code';
import { ContentExternalRefChip } from '@/components/widget/content-external-ref-chip';
import { ContentObligationsBadge } from '@/components/widget/content-obligations-badge';
import { ContentRevisionBadge } from '@/components/widget/content-revision-badge';
import { MessageExtractPanel } from '@/components/widget/message-extract-panel';
import { ObligationBadge } from '@/components/widget/obligation-badge';
import { useContentAttachmentsForContents } from '@/hooks/useContentAttachmentQueries';
import { ContentSharingPanel } from '@/components/widget/content-sharing-panel';
import { ContentTitleEditable } from '@/components/widget/content-title-editable';
import { ProvenanceTouchList } from '@/components/provenance';
import { RevisionsPanel } from '@/components/revisions/RevisionsPanel';
import { buildContentParticipantsLabel, isContentPrivate } from '@/components/explore/ExploreEntryHelpers';
import { Speak2MePlayControl } from '@/components/widget/speak2me-play-control';
import { DetailsPageLoader } from '@/guards/DetailsPageComponents';
import {
  useContentAsyncTaskWatcher,
  useContentBulkDelete,
  useContentDetail,
  useContentRegenerate,
  useContentUpdate,
  useUser,
} from '@/hooks';
import { contentAttachmentKeys } from '@/hooks/useContentAttachmentQueries';
import { useQueryClient } from '@tanstack/react-query';
import { ContentAttachments } from '@/components/widget/content-attachments';
import { CANONICAL_LABELS, getCollectionLabel } from '@/lib/content-labels';
import { cn } from '@/lib/utils';
import { CONTENT_DESCRIPTION_REGEN_TASK_PREFIX } from '@/utils/content-utils';
import { type AsyncTask, useAsyncStore } from '@/store/asyncStore';
import { useDocumentScrollStore } from '@/store/documentScrollStore';
import { useViewContextStore } from '@/store/viewContextStore';
import { useContentProvenance } from '@/hooks/useProvenanceQueries';
import { useSetActiveProvenance } from '@/features/speak2me/useSetActiveProvenance';
import { downloadDocumentExport, getContent, type DocumentExportFormat } from '@/api/content-api';
import { assertToolSucceeded, updateTemplate as updateTemplateApi } from '@/api/templates-api';
import { contentKeys } from '@/hooks/useContentQueries';
import { DetailsTemplate } from './DetailsTemplate';
import { toContentHref } from '@/utils/content-ref';
import { useQuery } from '@tanstack/react-query';
import { Network, Share2, Trash2 } from 'lucide-react';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Navigate, useLocation, useOutletContext, useParams } from 'react-router-dom';
import { MESSAGE_OPEN_OBLIGATIONS_EVENT } from '@/components/widget/message-events';
import { DetailsDocument } from './DetailsDocument';
import { DetailsAnchor } from './DetailsAnchor';
import { DetailsEvent } from './DetailsEvent';
import { DetailsFile } from './DetailsFile';
import { DetailsPhoto } from './DetailsPhoto';
import { DetailsRecording } from './DetailsRecording';
import { DetailsVideo } from './DetailsVideo';
import { DetailsUrl } from './DetailsUrl';
import { DetailsProvenance } from './DetailsProvenance';
import { DetailsMessage } from './DetailsMessage';
import { MessageDraftPage } from '@/features/messaging/MessageDraftPage';

const MESSAGE_TOGGLE_DRAFT_EVENT = 'vivport:message-toggle-draft-composer';
const MESSAGE_TOGGLE_PROMOTE_EVENT = 'vivport:message-toggle-promote-panel';
const MESSAGE_RUN_EXTRACTION_EVENT = 'vivport:message-run-extraction';

import {
  getDefaultTitleForContentType,
  getScreenForContentType,
  isEditableContentType,
  resolveMessageThreadTitleFromDetail,
} from './content-routing';
import { DetailsContentActionButtons } from './DetailsContentActionButtons';

/**
 * Main wrapper component for all detail pages
 * - Gets contentId from URL params
 * - Fetches content using useContentDetail
 * - Provides shared handlers for title/description updates
 * - Handles regeneration logic and action buttons
 * - Manages layout state and renders layout UI (title, code, anchor status, actions)
 * - Determines contentType and routes to appropriate renderer
 */
export function DetailsContent() {
  const params = useParams<{ contentRef: string }>();
  const { setScreen } = useOutletContext<ExploreOutletContext>();
  const { data: memberContext } = useUser();
  const memberId = memberContext?.selectedMember?.id ?? 0;
  
  // Layout state management (previously in LayoutDetails)
  const [layoutData, setLayoutData] = useState({
    title: '',
    code: null as string | null,
    anchorTitle: null as string | null,
    createdAt: new Date(),
    onTitleSave: undefined as ((title: string) => Promise<void> | void) | undefined,
    actionButtons: null as React.ReactNode
  });
  const [isShareOpen, setIsShareOpen] = useState(false);
  
  // Extract contentId from params
  const contentRef = String(params.contentRef ?? '').trim();

  // Fetch content using React Query
  const { data: content, isLoading, isError } = useQuery({
    queryKey: ['content-ref', contentRef],
    queryFn: () => getContent(contentRef),
    enabled: contentRef.length > 0,
  });

  const parentAnchorId =
    content && 'parentAnchorId' in content ? content.parentAnchorId : 0;
  const { data: parentAnchorContent } = useContentDetail(parentAnchorId);

  const updateContentMutation = useContentUpdate();
  const regenerateMutation = useContentRegenerate();
  const tasks = useAsyncStore((s) => s.tasks);
  const addTask = useAsyncStore((s) => s.addTask);
  const updateTask = useAsyncStore((s) => s.updateTask);
  const setActiveProvenance = useSetActiveProvenance();
  const provenanceQuery = useContentProvenance(content?.code);
  const [revisionsOpen, setRevisionsOpen] = useState(false);
  const [provenanceListOpen, setProvenanceListOpen] = useState(false);

  const obligationSource =
    content && (content.contentType === 'MESSAGE' || content.contentType === 'EVENT')
      ? content
      : null;
  const obligationContentIds = useMemo(() => {
    if (!obligationSource) return [];
    if (obligationSource.contentType === 'MESSAGE') {
      const threadIds = (obligationSource.conversation?.items ?? []).map((item) => item.id);
      return [...new Set([obligationSource.id, ...threadIds])];
    }
    return [obligationSource.id];
  }, [obligationSource]);
  const obligationAttachments = useContentAttachmentsForContents(obligationContentIds);
  const obligationExtractIds = useMemo(
    () =>
      obligationAttachments.items
        .filter((item) => item.contentType === 'MESSAGE_EXTRACT')
        .map((item) => item.id),
    [obligationAttachments.items]
  );
  const obligationCount = obligationExtractIds.length;
  const [obligationsListOpen, setObligationsListOpen] = useState(false);
  const location = useLocation();
  const consumedNavStateKey = useRef<string | null>(null);
  useEffect(() => {
    const open = () => setObligationsListOpen(true);
    window.addEventListener(MESSAGE_OPEN_OBLIGATIONS_EVENT, open);
    return () => window.removeEventListener(MESSAGE_OPEN_OBLIGATIONS_EVENT, open);
  }, []);
  useEffect(() => {
    if (consumedNavStateKey.current === location.key) {
      return;
    }
    consumedNavStateKey.current = location.key;
    if ((location.state as { openObligations?: unknown } | null)?.openObligations === true) {
      setObligationsListOpen(true);
    }
  }, [location.key, location.state]);
  const queryClient = useQueryClient();
  const bulkDeleteMutation = useContentBulkDelete();

  const handleDeleteObligations = useCallback(async () => {
    if (!obligationSource) return;
    const obligationIds = obligationExtractIds;
    if (obligationIds.length === 0) return;
    if (!window.confirm(`Delete ${obligationIds.length} obligation(s) from this content?`)) {
      return;
    }
    try {
      const result = await bulkDeleteMutation.mutateAsync(obligationIds);
      for (const contentId of obligationContentIds) {
        queryClient.invalidateQueries({
          queryKey: contentAttachmentKeys.byParent(contentId),
        });
      }
      const leftBehind = result.skipped.length + result.failed.length;
      // deletedNodeCount counts every logical node removed, which can exceed the
      // obligations asked for when one carries a structurally inseparable child — so
      // this says "item", not "obligation".
      const deleted = `${result.deletedNodeCount} item${result.deletedNodeCount === 1 ? '' : 's'}`;
      if (leftBehind === 0) {
        toast.success(`${deleted} deleted.`);
      } else if (result.deletedNodeCount === 0) {
        toast.error(
          `Nothing was deleted. ${leftBehind} item${leftBehind === 1 ? '' : 's'} could not be removed.`
        );
      } else {
        toast.warning(`${deleted} deleted. ${leftBehind} could not be removed.`);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete obligations.');
    }
  }, [obligationSource, obligationExtractIds, obligationContentIds, bulkDeleteMutation, queryClient]);

  useContentAsyncTaskWatcher(tasks, updateTask);

  // Store content in ref for stable callback references
  const contentDataRef = useRef(content);
  useEffect(() => {
    contentDataRef.current = content;
  }, [content]);

  // Shared handler for title updates - stable reference
  const handleTitleSave = useCallback(
    async (title: string) => {
      const currentContent = contentDataRef.current;
      if (!currentContent || !isEditableContentType(currentContent.contentType)) {
        return;
      }
      // A template's name lives on its tag as well as its title, and only the governed tool
      // renames both together — the generic content update would silently split them.
      if (currentContent.contentType === 'TEMPLATE') {
        assertToolSucceeded(
          await updateTemplateApi({ templateId: currentContent.id, name: title })
        );
        await queryClient.invalidateQueries({ queryKey: contentKeys.details(), refetchType: 'all' });
        setLayoutData(prev => ({ ...prev, title }));
        return;
      }
      await updateContentMutation.mutateAsync({
        id: currentContent.id,
        data: {
          contentType: currentContent.contentType,
          title
        }
      });
      // Update local layout state
      setLayoutData(prev => ({ ...prev, title }));
    },
    [updateContentMutation, queryClient]
  );

  const onTitleSave = layoutData.onTitleSave;
  const handleToggleMessageDraftComposer = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(MESSAGE_TOGGLE_DRAFT_EVENT));
    }
  }, []);
  const handleToggleMessagePromotePanel = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(MESSAGE_TOGGLE_PROMOTE_EVENT));
    }
  }, []);
  const handleRunMessageExtraction = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(MESSAGE_RUN_EXTRACTION_EVENT));
    }
  }, []);
  
  // Wrapper for title save that updates layout state
  const handleTitleSaveWrapper = useCallback(
    async (newTitle: string) => {
      if (!onTitleSave) return;
      await onTitleSave(newTitle);
      setLayoutData(prev => ({ ...prev, title: newTitle }));
    },
    [onTitleSave]
  );

  // Shared handler for description updates - stable reference
  const handleDescriptionSave = useCallback(
    async (description: string) => {
      const currentContent = contentDataRef.current;
      if (currentContent && isEditableContentType(currentContent.contentType)) {
        await updateContentMutation.mutateAsync({
          id: currentContent.id,
          data: { 
            contentType: currentContent.contentType,
            description 
          }
        });
      }
    },
    [updateContentMutation]
  );

  // Shared regeneration handler - stable reference
  const handleRegenerate = useCallback(() => {
    const currentContent = contentDataRef.current;
    if (!currentContent) {
      return;
    }

    const instance = nanoid(8);
    const queueContentId = `${CONTENT_DESCRIPTION_REGEN_TASK_PREFIX}-${currentContent.id}-${instance}`;
    const queueContentType: NonNullable<AsyncTask['contentType']> =
      currentContent.contentType === 'PHOTO'
        ? 'photo'
        : currentContent.contentType === 'RECORDING'
          ? 'recording'
          : (currentContent.contentType as NonNullable<AsyncTask['contentType']>);

    const taskId = addTask({
      title: `Regenerate description · ${currentContent.title?.trim() || currentContent.code || `#${currentContent.id}`}`,
      description: 'Regenerating description…',
      status: 'processing',
      progress: 0,
      contentType: queueContentType,
      contentId: queueContentId,
      payload: { remoteContentId: currentContent.id },
    });

    regenerateMutation.mutate(
      { id: currentContent.id, awaitLLM: true },
      {
        onSuccess: () => {
          updateTask(taskId, {
            status: 'completed',
            progress: 100,
            description: 'Description updated',
          });
        },
        onError: (error) => {
          updateTask(taskId, {
            status: 'failed',
            description: 'Regeneration failed',
            error: error instanceof Error ? error.message : 'Unknown error',
          });
        },
      }
    );
  }, [addTask, regenerateMutation, updateTask]);

  const [isExporting, setIsExporting] = useState(false);
  const handleExport = useCallback(async (format: DocumentExportFormat) => {
    const currentContent = contentDataRef.current;
    if (!currentContent || currentContent.contentType !== 'DOCUMENT') return;
    setIsExporting(true);
    try {
      await downloadDocumentExport(currentContent.id, format, currentContent.title ?? undefined);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to export document');
    } finally {
      setIsExporting(false);
    }
  }, []);

  // Check if content is being regenerated (content-type specific logic)
  const isRegenerating = useMemo(() => {
    if (!content) return false;

    const descriptionRegenActive = tasks.some(
      (t) =>
        (t.status === 'pending' || t.status === 'processing') &&
        typeof t.contentId === 'string' &&
        t.contentId.startsWith(`${CONTENT_DESCRIPTION_REGEN_TASK_PREFIX}-${content.id}-`) &&
        t.payload?.remoteContentId === content.id
    );

    if (descriptionRegenActive || regenerateMutation.isPending) {
      return true;
    }

    switch (content.contentType) {
      case 'ANCHOR':
        return tasks.some(
          (t) =>
            t.contentType === 'ANCHOR' &&
            t.status === 'processing' &&
            t.payload?.anchorId === content.id
        );
      case 'EVENT':
        return tasks.some(
          (t) =>
            t.contentType === 'EVENT' &&
            t.status === 'processing' &&
            t.payload?.eventId === content.id
        );
      case 'DOCUMENT':
        return tasks.some(
          (t) =>
            t.contentType === 'DOCUMENT' &&
            t.status === 'processing' &&
            t.payload?.documentId === content.id
        );
      case 'PHOTO':
        return tasks.some(
          (t) =>
            t.contentType === 'photo' &&
            t.status === 'processing' &&
            t.payload?.photoId === content.id &&
            t.contentId &&
            typeof t.contentId === 'string' &&
            t.contentId.includes('photo-regenerate-')
        );
      case 'RECORDING':
        return tasks.some(
          (t) =>
            t.contentType === 'recording' &&
            t.status === 'processing' &&
            t.payload?.recordingId === content.id &&
            t.contentId &&
            typeof t.contentId === 'string' &&
            t.contentId.includes('recording-regenerate-')
        );
      case 'NEO_CHAT':
        return false;
      case 'MESSAGE':
        return false;
      case 'MANUAL_SESSION':
        return false;
      default:
        return false;
    }
  }, [content, tasks, regenerateMutation.isPending]);

  // Generate action buttons - memoized
  const actionButtons = useMemo(() => {
    if (!content) return null;
    return (
      <DetailsContentActionButtons
        content={content}
        isRegenerating={isRegenerating}
        isExporting={isExporting}
        handleExport={handleExport}
        handleRegenerate={handleRegenerate}
        handleToggleMessageDraftComposer={handleToggleMessageDraftComposer}
        handleToggleMessagePromotePanel={handleToggleMessagePromotePanel}
        handleRunMessageExtraction={handleRunMessageExtraction}
      />
    );
  }, [
    content,
    handleExport,
    handleRegenerate,
    handleToggleMessageDraftComposer,
    handleToggleMessagePromotePanel,
    handleRunMessageExtraction,
    isExporting,
    isRegenerating,
  ]);

  // Determine what to show for anchor status
  const getAnchorStatusText = () => {
    if (content?.contentType === 'ANCHOR') {
      return `Root ${CANONICAL_LABELS.anchor}`;
    }
    if (layoutData.anchorTitle) {
      return layoutData.anchorTitle;
    }
    return `No ${CANONICAL_LABELS.anchor} Linked`;
  };

  const anchorDescendantCount: number | null = (() => {
    if (!content) return null;
    if (content.contentType === 'ANCHOR') return content.descendantCount;
    if (parentAnchorContent?.contentType === 'ANCHOR') return parentAnchorContent.descendantCount;
    return null;
  })();

  const activeSectionCode = useDocumentScrollStore((s) => s.activeSectionCode);
  const viewContentCode = typeof content?.code === 'string' ? content.code : undefined;
  const viewContentType = content?.contentType;
  useEffect(() => {
    if (viewContentCode === undefined) return;
    const sectionCode =
      viewContentType === 'DOCUMENT' && activeSectionCode !== null ? activeSectionCode : undefined;
    useViewContextStore.getState().setSlot('content', {
      code: viewContentCode,
      ...(sectionCode !== undefined ? { sectionCode } : {}),
    });
    return () => useViewContextStore.getState().clearSlot('content');
  }, [viewContentCode, viewContentType, activeSectionCode]);

  // Update screen context when content changes
  useEffect(() => {
    if (!content) {
      setScreen(null);
      return;
    }

    const screenName = getScreenForContentType(content.contentType);
    if (screenName) {
      setScreen({ screen: screenName, id: content.id });
    }

    return () => {
      setScreen(null);
    };
  }, [content, setScreen]);

  // Update layout data when content changes
  useEffect(() => {
    if (!content) return;

    const getTitle = () => {
      if (content.contentType === 'MESSAGE') {
        const normalizedThreadTitle = resolveMessageThreadTitleFromDetail(content);
        if (normalizedThreadTitle) {
          return normalizedThreadTitle;
        }
      }
      if (content.title) return content.title;

      return getDefaultTitleForContentType(
        content.contentType,
        content.code,
        content.contentType === 'COLLECTION' ? content.kind : undefined
      );
    };

    const getAnchorTitle = () => {
      if (content.contentType === 'ANCHOR') return null;
      return ('parentAnchorTitle' in content ? content.parentAnchorTitle : null) || null;
    };

    setLayoutData({
      title: getTitle(),
      code: content.code ?? null,
      anchorTitle: getAnchorTitle(),
      createdAt: new Date(content.originCreatedAt),
      onTitleSave: handleTitleSave,
      actionButtons,
    });
  }, [content, actionButtons, handleTitleSave]);

  // Handle invalid ID
  if (!contentRef) {
    return <Navigate to="/explore" replace />;
  }
  
  // Handle loading state
  if (isLoading) {
    return <DetailsPageLoader key={`loader-${contentRef}`} />;
  }
  
  // Handle error state
  if (isError) {
    return <Navigate to="/explore" replace />;
  }
  
  // Handle no content
  if (!content) {
    return <Navigate to="/explore" replace />;
  }

  // MessageExtracts have no standalone page — redirect to the source communication.
  if (content.contentType === 'MESSAGE_EXTRACT') {
    const sourceContentId = content.sourceHighlight?.sourceContentId;
    return (
      <Navigate
        to={sourceContentId ? toContentHref(String(sourceContentId)) : '/explore'}
        replace
      />
    );
  }
  
  // Route to type-specific renderer - pass shared handlers and state
  const sharedProps = {
    onDescriptionSave: handleDescriptionSave,
    isRegenerating,
  };
  const isMessageContent = content.contentType === 'MESSAGE';
  
  // Render layout UI and content
  return (
    <div className="flex min-h-0 min-w-0 flex-col">
      <ExploreActions />
      <ExploreNavBreadcrumbs />

      <div className="min-w-0 overflow-x-hidden">
        <div className="min-w-0 px-4 pt-4">
          {/* Large devices: stacked layout */}
          <div className="hidden lg:block">
            {/* Top row: Anchor label and action buttons */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 min-w-0">
                {getAnchorStatusText() && (
                  <p className="text-sm text-muted-foreground truncate">
                    {getAnchorStatusText()}
                  </p>
                )}
                {anchorDescendantCount !== null && (
                  <span
                    className="flex items-center gap-1 text-sm text-muted-foreground flex-shrink-0"
                    title={`Descendants of ${CANONICAL_LABELS.anchor.toLowerCase()}`}
                  >
                    <Network className="h-4 w-4" />
                    {anchorDescendantCount}
                  </span>
                )}
              </div>
              {layoutData.actionButtons && (
                <div className="flex items-center gap-2 flex-shrink-0">{layoutData.actionButtons}</div>
              )}
            </div>

            {/* Title row: Full width */}
            <div className="mb-4">
              <div className="flex min-w-0 items-center gap-2">
                {content.contentType === 'NEO_CHAT' ? (
                  <Speak2MePlayControl
                    onOpen={() => {
                      setActiveProvenance({ id: content.id, type: 'NEO_CHAT', code: content.code });
                    }}
                  />
                ) : null}
                <ObligationBadge contentId={content.id} rollup={content.obligationRollup} />
                <div className="min-w-0 flex-1">
                  {isMessageContent ? (
                    <h1 className="truncate text-lg font-medium">{layoutData.title}</h1>
                  ) : (
                    <ContentTitleEditable
                      title={layoutData.title}
                      onSave={handleTitleSaveWrapper}
                      className="text-lg font-medium"
                    />
                  )}
                </div>
              </div>
              <div className="mt-2 flex items-center gap-3">
                {layoutData.code && <ContentCode code={layoutData.code} />}
                {content.contentType === 'FILE' ? (
                  <ContentExternalRefChip externalRefs={content.externalRefs} />
                ) : null}
                {isContentPrivate(content) ? (
                  <span className="text-sm text-muted-foreground">Private</span>
                ) : (
                  <span className="truncate text-sm text-muted-foreground min-w-0">
                    {buildContentParticipantsLabel(content)}
                  </span>
                )}
                <ContentObligationsBadge
                  count={obligationCount}
                  onClick={() => setObligationsListOpen((o) => !o)}
                />
                {obligationCount > 0 ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                    aria-label="Delete all obligations"
                    title="Delete all obligations"
                    onClick={handleDeleteObligations}
                    disabled={bulkDeleteMutation.isPending}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
                <ContentActivityBadge
                  count={provenanceQuery.data?.totalCount ?? 0}
                  onClick={() => setProvenanceListOpen((o) => !o)}
                />
                <ContentRevisionBadge
                  revision={content.revision}
                  onClick={() => setRevisionsOpen(true)}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Share"
                  aria-pressed={isShareOpen}
                  onClick={() => setIsShareOpen((v) => !v)}
                  className={cn('h-7 w-7', isShareOpen && 'bg-accent text-accent-foreground')}
                >
                  <Share2 className="h-4 w-4" />
                </Button>
                <p className="ml-auto text-sm text-muted-foreground">{layoutData.createdAt.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Small/Medium devices: stacked layout */}
          <div className="lg:hidden">
            {/* Anchor label */}
            <div className="flex items-center gap-2 mb-2 min-w-0">
              {getAnchorStatusText() && (
                <p className="text-sm text-muted-foreground truncate">
                  {getAnchorStatusText()}
                </p>
              )}
              {anchorDescendantCount !== null && (
                <span
                  className="flex items-center gap-1 text-sm text-muted-foreground flex-shrink-0"
                  title={`Descendants of ${CANONICAL_LABELS.anchor.toLowerCase()}`}
                >
                  <Network className="h-4 w-4" />
                  {anchorDescendantCount}
                </span>
              )}
            </div>

            {/* Title row: Full width */}
            <div className="mb-4">
              <div className="flex min-w-0 items-center gap-2">
                {content.contentType === 'NEO_CHAT' ? (
                  <Speak2MePlayControl
                    onOpen={() => {
                      setActiveProvenance({ id: content.id, type: 'NEO_CHAT', code: content.code });
                    }}
                  />
                ) : null}
                <ObligationBadge contentId={content.id} rollup={content.obligationRollup} />
                <div className="min-w-0 flex-1">
                  {isMessageContent ? (
                    <h1 className="truncate text-lg font-medium">{layoutData.title}</h1>
                  ) : (
                    <ContentTitleEditable
                      title={layoutData.title}
                      onSave={handleTitleSaveWrapper}
                      className="text-lg font-medium"
                    />
                  )}
                </div>
              </div>
              <div className="mt-2 flex items-center gap-3">
                {layoutData.code && <ContentCode code={layoutData.code} />}
                {content.contentType === 'FILE' ? (
                  <ContentExternalRefChip externalRefs={content.externalRefs} />
                ) : null}
                {isContentPrivate(content) ? (
                  <span className="text-sm text-muted-foreground">Private</span>
                ) : (
                  <span className="truncate text-sm text-muted-foreground min-w-0">
                    {buildContentParticipantsLabel(content)}
                  </span>
                )}
                <ContentObligationsBadge
                  count={obligationCount}
                  onClick={() => setObligationsListOpen((o) => !o)}
                />
                {obligationCount > 0 ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                    aria-label="Delete all obligations"
                    title="Delete all obligations"
                    onClick={handleDeleteObligations}
                    disabled={bulkDeleteMutation.isPending}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
                <ContentActivityBadge
                  count={provenanceQuery.data?.totalCount ?? 0}
                  onClick={() => setProvenanceListOpen((o) => !o)}
                />
                <ContentRevisionBadge
                  revision={content.revision}
                  onClick={() => setRevisionsOpen(true)}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Share"
                  aria-pressed={isShareOpen}
                  onClick={() => setIsShareOpen((v) => !v)}
                  className={cn('h-7 w-7', isShareOpen && 'bg-accent text-accent-foreground')}
                >
                  <Share2 className="h-4 w-4" />
                </Button>
                <p className="ml-auto text-sm text-muted-foreground">{layoutData.createdAt.toLocaleString()}</p>
              </div>
            </div>
            
            {/* Action buttons */}
            {layoutData.actionButtons && (
              <div className="mb-4">{layoutData.actionButtons}</div>
            )}
          </div>
        </div>
        
        {isShareOpen && (
          <div className="px-4 pb-2">
            <ContentSharingPanel contentId={content.id} canEdit={true} />
          </div>
        )}

        {provenanceListOpen && provenanceQuery.data && provenanceQuery.data.items.length > 0 && (
          <div className="px-4 pb-2">
            <ProvenanceTouchList
              items={provenanceQuery.data.items}
              onSelect={(t) =>
                setActiveProvenance({
                  id: t.provenanceId,
                  type: t.provenanceType,
                  code: t.provenanceCode,
                })
              }
            />
          </div>
        )}

        <RevisionsPanel
          contentId={content.id}
          code={content.code}
          title={layoutData.title}
          open={revisionsOpen}
          onOpenChange={setRevisionsOpen}
        />

        {obligationsListOpen && obligationSource && obligationCount > 0 && (
          <div className="px-4 pb-2">
            <MessageExtractPanel
              sourceContentIds={obligationContentIds}
              parties={obligationSource.parties}
            />
          </div>
        )}

        <div className="min-w-0 px-4 pb-6 pt-2">
          {(() => {
            switch (content.contentType) {
              case 'ANCHOR':
                return <DetailsAnchor key={`anchor-${contentRef}`} content={content} {...sharedProps} />;
              case 'EVENT':
                return (
                  <DetailsEvent
                    key={`event-${contentRef}`}
                    content={content}
                    {...sharedProps}
                    memberId={memberId}
                  />
                );
              case 'DOCUMENT':
                return (
                  <DetailsDocument
                    key={`document-root-${content.rootCode ?? content.code}`}
                    content={content}
                    isRegenerating={isRegenerating}
                  />
                );
              case 'PHOTO':
                return <DetailsPhoto key={`photo-${contentRef}`} content={content} {...sharedProps} />;
              case 'RECORDING':
                return <DetailsRecording key={`recording-${contentRef}`} content={content} {...sharedProps} />;
              case 'VIDEO':
                return <DetailsVideo key={`video-${contentRef}`} content={content} {...sharedProps} />;
              case 'FILE':
                return <DetailsFile key={`file-${contentRef}`} content={content} {...sharedProps} />;
              case 'URL':
                return <DetailsUrl key={`url-${contentRef}`} content={content} {...sharedProps} />;
              case 'NEO_CHAT':
              case 'PROCESS_RUN':
              case 'MANUAL_SESSION':
                return (
                  <DetailsProvenance
                    key={`prov-${contentRef}`}
                    content={content}
                    {...sharedProps}
                  />
                );
              case 'MESSAGE':
                return content.conversation?.composeDraft ? (
                  <MessageDraftPage
                    key={`draft-${contentRef}`}
                    content={content}
                    composeDraft={content.conversation.composeDraft}
                  />
                ) : (
                  <DetailsMessage
                    key={`message-${contentRef}`}
                    content={content}
                    memberId={memberId}
                  />
                );
              case 'COLLECTION':
                // A collection is a provenance record whose whole content is its children, so
                // the generic attachment list IS the surface. `embedded` is deliberate: it
                // turns off the manage affordances, because membership changes only through
                // content.moveObservationMedia and an Attach button here could not succeed.
                return (
                  <ContentAttachments
                    key={`collection-${contentRef}`}
                    parentContentId={content.id}
                    title="Attachments"
                    emptyText="No attachments yet"
                    breadcrumbLabel={getCollectionLabel(content.kind)}
                    layout="embedded"
                  />
                );
              case 'TEMPLATE':
                return (
                  <DetailsTemplate
                    key={`template-${contentRef}`}
                    content={content}
                    onDescriptionSave={sharedProps.onDescriptionSave}
                  />
                );
              default:
                return <Navigate to="/explore" replace />;
            }
          })()}
        </div>
      </div>
    </div>
  );
}


